/**
 * formatIST — small shared IST (Asia/Kolkata) date/time helpers.
 *
 * Backend timestamps are already IST-offset ISO strings (e.g.
 * `2026-06-21T19:09:23.430560+05:30`). These render them consistently in IST
 * regardless of the viewer's machine timezone. Seconds are included because, on
 * the lab bench, two samples can be taken seconds apart and the exact second
 * matters.
 */

const IST = { timeZone: 'Asia/Kolkata' };

// e.g. "21 Jun, 07:09:23 pm" — date + time WITH seconds, in IST.
export function formatDateTimeIST(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  try {
    return d.toLocaleString('en-IN', {
      ...IST,
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
  } catch {
    return '';
  }
}

// e.g. "21 Jun 2026" — date only, in IST. Built by fixed-offset arithmetic, not
// toLocaleDateString: en-IN locale data varies by runtime (newer CLDR renders
// "21-Jun-2026" and "Sept"), so the ratified format would differ per browser.
export function formatDateIST(iso) {
  if (!iso) return '';
  const utcMs = Date.parse(iso);
  if (isNaN(utcMs)) return '';
  const ist = new Date(utcMs + IST_OFFSET_MS);
  const dd = String(ist.getUTCDate()).padStart(2, '0');
  return `${dd} ${MONTHS_SHORT[ist.getUTCMonth()]} ${ist.getUTCFullYear()}`;
}

// e.g. "16 Oct 2026, 04:05:40 am" — date WITH year + time with seconds, in IST (HIU consent surfaces, R135).
// Separate from formatDateTimeIST (no year) so its other callers are unchanged.
export function formatDateTimeISTWithYear(iso) {
  const date = formatDateIST(iso);
  if (!date) return '';
  try {
    const time = new Date(iso).toLocaleTimeString('en-IN', {
      ...IST, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    return `${date}, ${time}`;
  } catch {
    return '';
  }
}

// e.g. "16 Oct 2026, 10:42 am" — date WITH year + time, no seconds, in IST (HIP send list).
export function formatDateTimeISTWithYearNoSeconds(iso) {
  const date = formatDateIST(iso);
  if (!date) return '';
  try {
    const time = new Date(iso).toLocaleTimeString('en-IN', {
      ...IST, hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return `${date}, ${time}`;
  } catch {
    return '';
  }
}

// e.g. "10:42 am" - the time of day alone, no seconds, in IST (the same time part as formatDateTimeISTWithYearNoSeconds).
export function formatTimeIST(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  try {
    return d.toLocaleTimeString('en-IN', {
      ...IST, hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return '';
  }
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * formatDayIST — formats the server's `day_ist` field (`YYYY-MM-DD`, a
 * calendar day, not a timestamp) as "21 Jun 2026". Parses the string
 * directly instead of going through `Date`/timezone conversion — there is no
 * time-of-day or offset on this field, so no conversion can get it wrong.
 * Returns "—" for null/unparseable input (PATIENT-CARD R1-6: an unknown
 * date renders as a dash, never a blank).
 */
export function formatDayIST(dayIst) {
  if (!dayIst || typeof dayIst !== 'string') return '—';
  const [y, m, d] = dayIst.split('-');
  const monthIndex = Number(m) - 1;
  if (!y || !d || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return '—';
  return `${d} ${MONTHS_SHORT[monthIndex]} ${y}`;
}

// Fixed IST offset (Asia/Kolkata has no DST) — used ONLY for the two
// arithmetic helpers below, which never touch the host machine's timezone.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/**
 * DISCHARGE-TEMPLATES Phase 1 §1a (bk-646 lesson) — the discharge summary's
 * `admitted_at`/`discharged_at` header overrides are UTC ISO ms `"...Z"` on
 * the wire; the `<input type="datetime-local">` shows/edits their IST
 * wall-clock value. Both helpers do the IST<->UTC conversion by FIXED-OFFSET
 * ARITHMETIC on epoch milliseconds — never by constructing a `Date` from a
 * naive (timezone-less) string, which V8 parses in the HOST's local
 * timezone and is correct only by accident on an IST machine. That banned
 * pattern is exactly `new Date(naiveString)`; it is not used here.
 */

// UTC ISO ms "...Z" -> IST wall-clock input value "YYYY-MM-DDTHH:mm" (prefill -> input).
export function utcIsoToISTInputValue(iso) {
  if (!iso) return '';
  const utcMs = Date.parse(iso);
  if (isNaN(utcMs)) return '';
  const ist = new Date(utcMs + IST_OFFSET_MS);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = ist.getUTCFullYear();
  const mm = pad(ist.getUTCMonth() + 1);
  const dd = pad(ist.getUTCDate());
  const hh = pad(ist.getUTCHours());
  const min = pad(ist.getUTCMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

// IST wall-clock input value "YYYY-MM-DDTHH:mm[:ss]" -> UTC ISO ms "...Z" (input -> wire).
export function istInputValueToUtcIso(naive) {
  if (!naive) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(naive);
  if (!m) return null;
  const [, y, mo, d, h, min, s] = m;
  const asUtcMs = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(min), Number(s || 0));
  const trueUtcMs = asUtcMs - IST_OFFSET_MS;
  return new Date(trueUtcMs).toISOString();
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// IST calendar-date index (whole days since epoch, IST wall-clock) — the
// time-of-day is dropped once the calendar date is resolved. Same fixed
// -offset arithmetic as the two helpers above (never `new Date(naiveString)`).
//
// EXPORTED for DOD rule v3 (C5): "same IST calendar day?" is now a product
// rule (the Complete step is silent on the same day, asks on a different
// one), and it must be answered by the SAME helper the length-of-stay
// arithmetic below already uses — never by a second, `toISOString()`-based
// day compare, which mis-buckets every instant between 00:00 and 05:30 IST.
export function istCalendarDayIndex(iso) {
  if (!iso) return null;
  const utcMs = Date.parse(iso);
  if (isNaN(utcMs)) return null;
  return Math.floor((utcMs + IST_OFFSET_MS) / MS_PER_DAY);
}

/**
 * DISCHARGE-TEMPLATES Phase 1 addendum F — the discharge summary's
 * `length_of_stay` header field: "n day(s)" from the IST CALENDAR DATE of
 * admission vs discharge. Matches the server's own computed value so a
 * client-side recompute (`SummaryFields.jsx`, while the field is untouched)
 * never disagrees with what the server would compute on save. Same IST
 * calendar date -> "1 day" (never "0 days" or negative); otherwise the exact
 * number of IST calendar dates spanned, e.g. 16 Aug -> 17 Sep is "32 days",
 * and 23:00 -> 01:00 the very next calendar date is "1 day" (one calendar
 * date apart), NOT "2" — this is the FROZEN rule shared with the backend
 * (LOS-BE), one line, no special-casing beyond the same-day floor. Returns
 * `null` when either input is missing/unparseable — the caller falls back
 * to the server's own `prefill`.
 */
export function computeLengthOfStay(admittedIso, dischargedIso) {
  const admitDay = istCalendarDayIndex(admittedIso);
  const dischargeDay = istCalendarDayIndex(dischargedIso);
  if (admitDay === null || dischargeDay === null) return null;
  const diff = dischargeDay - admitDay;
  const days = diff <= 0 ? 1 : diff;
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Precise elapsed time since `iso`, seconds-accurate when recent — for the
 * technician's "how long has this been waiting" view: "1h 04m", "12m 30s",
 * "23s". Pass a shared `nowMs` (one ticking clock) so every row updates in
 * lockstep without per-row timers. Falls back to the current time if omitted.
 */
export function elapsedSince(iso, nowMs) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (isNaN(t)) return '';
  let s = Math.floor(((nowMs == null ? Date.now() : nowMs) - t) / 1000);
  if (s < 0) s = 0;
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h >= 1) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  if (m >= 1) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${s}s`;
}
